/**
 * Order Funnel Dashboard for BPIO (Binder Presale Intelligent Orchestrator)
 *
 * This component provides a real-time view of order processing through the BPIO pipeline:
 * 1. Uploaded - Orders uploaded and parsed
 * 2. Credit Checked - Credit analysis completed
 * 3. AI Verifying - Ruyah AI agent verification in progress
 * 4. Verified - Customer confirmed via WhatsApp
 * 5. Ready to Route - Orders ready for logistics
 *
 * Features:
 * - Real-time order status tracking
 * - Order dropout funnel visualization
 * - AI conversation feed
 * - Risk score heatmap
 * - Export functionality
 */

import React, { useState, useEffect } from 'react';
import './OrderDashboard.css';

const OrderDashboard = () => {
  const [orders, setOrders] = useState([]);
  const [funnelStats, setFunnelStats] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [demoMode, setDemoMode] = useState(true);  // Default to demo mode
  const [verifyingOrder, setVerifyingOrder] = useState(null);  // Track which order is being verified
  const [sendingTestMessage, setSendingTestMessage] = useState(false);  // Track test message sending

  // Fetch order funnel statistics using Cloudflare Pages proxy (relative URL)
  useEffect(() => {
    let isFirstFetch = true;

    const calculateFunnelStats = (ordersList) => {
      const stats = {
        uploaded: 0,
        credit_checked: 0,
        ai_verifying: 0,
        verified: 0,
        ready_to_route: 0,
        in_delivery: 0,
        completed: 0,
        rejected: 0,
      };

      if (Array.isArray(ordersList)) {
        ordersList.forEach(order => {
          if (order && order.status) {
            stats[order.status] = (stats[order.status] || 0) + 1;
          }
        });
      }

      setFunnelStats(stats);
    };

    const fetchFunnelStats = async () => {
      try {
        // Only show loading on first fetch, not on updates
        if (isFirstFetch) {
          setLoading(true);
        }

        // Use demo mode to show test data
        const url = demoMode ? '/api/v1/orders?demo=true' : '/api/v1/orders';
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        // Backend returns { orders: [], total: 0 } or with demo data
        const ordersList = Array.isArray(data) ? data : (data.orders || []);
        calculateFunnelStats(ordersList);
        setOrders(ordersList);
        setError(null);
      } catch (err) {
        console.error('Fetch error:', err);
        setError(err.message);
      } finally {
        // Only hide loading on first fetch
        if (isFirstFetch) {
          setLoading(false);
          isFirstFetch = false;
        }
      }
    };

    // Initial fetch
    fetchFunnelStats();

    // Set up interval for updates (only after initial fetch)
    const interval = setInterval(fetchFunnelStats, 5000);
    return () => clearInterval(interval);
  }, []);

  // Fetch active conversations using Cloudflare Pages proxy (relative URL)
  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const response = await fetch('/api/v1/ruyah/conversations');

        if (!response.ok) {
          console.warn(`Conversations API error: ${response.status}`);
          return;
        }

        const data = await response.json();
        setConversations(data.conversations || []);
      } catch (err) {
        console.error('Failed to fetch conversations:', err);
      }
    };

    fetchConversations();
    const interval = setInterval(fetchConversations, 3000);
    return () => clearInterval(interval);
  }, []);

  // Calculate dropout rates
  const calculateDropoutRate = (from, to) => {
    const fromCount = funnelStats[from] || 0;
    const toCount = funnelStats[to] || 0;
    const rate = fromCount > 0 ? ((fromCount - toCount) / fromCount * 100).toFixed(1) : 0;
    return parseFloat(rate);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR',
    }).format(amount);
  };

  // Risk score color indicator
  const getRiskColor = (score) => {
    if (score < 0.4) return '#10b981'; // green - low risk
    if (score < 0.6) return '#f59e0b'; // amber - moderate risk
    if (score < 0.8) return '#ef4444'; // red - high risk
    return '#7f1d1d'; // dark red - very high risk
  };

  // Send test WhatsApp message to verify Unifonic is working
  const handleSendTestMessage = async () => {
    setSendingTestMessage(true);

    try {
      const response = await fetch('/api/v1/test/send-buttons', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone_number: '+966506853876',
          customer_name: 'Test Customer',
          order_id: 'TEST-MSG-001',
          amount: 1000.00
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to send test message: ${response.status}`);
      }

      const data = await response.json();
      console.log('Test message sent:', data);

      alert(`✅ Test WhatsApp message sent to +966506853876\n\nIf you don't receive it in 10 seconds, Unifonic credentials may not be configured on the production server.\n\nMessage ID: ${data.message_id}`);
    } catch (err) {
      console.error('Test message error:', err);
      alert(`❌ Error sending test message: ${err.message}`);
    } finally {
      setSendingTestMessage(false);
    }
  };

  // Verify order and send WhatsApp message
  const handleVerifyOrder = async () => {
    if (!selectedOrder) return;

    setVerifyingOrder(selectedOrder.order_id);

    try {
      const response = await fetch('/api/v1/ruyah/verify/' + selectedOrder.order_id, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: selectedOrder.customer_id,
          customer_phone: selectedOrder.customer_phone,
          channel: 'whatsapp'
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`${response.status} ${response.statusText}: ${errorData.detail || ''}`);
      }

      const data = await response.json();
      console.log('Order verified:', data);

      // Show success message
      alert(`✅ WhatsApp message sent to ${selectedOrder.customer_phone}\n\nCustomer will receive a button to confirm payment collection.`);

      // Refresh orders
      setSelectedOrder(null);
    } catch (err) {
      console.error('Verification error:', err);
      alert(`❌ Error: ${err.message}\n\nNote: WhatsApp sending requires valid Unifonic credentials`);
    } finally {
      setVerifyingOrder(null);
    }
  };

  if (loading) {
    return (
      <div className="dashboard loading">
        <div className="spinner"></div>
        <p>Loading BPIO Dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div>
            <h1>BPIO Control Tower</h1>
            <p className="subtitle">Binder Presale Intelligent Orchestrator</p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {demoMode && <span style={{ fontSize: '12px', color: '#666', backgroundColor: '#f0f0f0', padding: '4px 8px', borderRadius: '4px' }}>DEMO MODE</span>}
            <button
              onClick={handleSendTestMessage}
              disabled={sendingTestMessage}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                backgroundColor: sendingTestMessage ? '#9ca3af' : '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: sendingTestMessage ? 'not-allowed' : 'pointer'
              }}
              title="Send a test WhatsApp message to verify Unifonic is working"
            >
              {sendingTestMessage ? 'Sending...' : '📱 Test Message'}
            </button>
            <button
              onClick={() => setDemoMode(!demoMode)}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                backgroundColor: demoMode ? '#3b82f6' : '#666',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {demoMode ? 'Switch to Live' : 'Switch to Demo'}
            </button>
          </div>
        </div>
        {error && <div className="error-banner">{error}</div>}
      </header>

      <main className="dashboard-main">
        {/* Funnel Visualization */}
        <section className="funnel-section">
          <h2>Order Processing Funnel</h2>
          <div className="funnel-container">
            <div className="funnel-stage">
              <div className="stage-header">
                <span className="stage-name">Uploaded</span>
                <span className="stage-count">{funnelStats.uploaded || 0}</span>
              </div>
              <div className="funnel-bar" style={{ width: '100%' }}>
                <div className="bar-fill" style={{ width: '100%', backgroundColor: '#3b82f6' }}></div>
              </div>
              <div className="stage-footer">100% baseline</div>
            </div>

            <div className="funnel-arrow">↓</div>

            <div className="funnel-stage">
              <div className="stage-header">
                <span className="stage-name">Credit Checked</span>
                <span className="stage-count">{funnelStats.credit_checked || 0}</span>
              </div>
              <div className="funnel-bar" style={{ width: `${funnelStats.uploaded > 0 ? ((funnelStats.credit_checked || 0) / funnelStats.uploaded * 100) : 0}%` }}>
                <div className="bar-fill" style={{ width: '100%', backgroundColor: '#06b6d4' }}></div>
              </div>
              <div className="stage-footer">
                {calculateDropoutRate('uploaded', 'credit_checked')}% dropout
              </div>
            </div>

            <div className="funnel-arrow">↓</div>

            <div className="funnel-stage">
              <div className="stage-header">
                <span className="stage-name">AI Verifying</span>
                <span className="stage-count">{funnelStats.ai_verifying || 0}</span>
              </div>
              <div className="funnel-bar" style={{ width: `${funnelStats.uploaded > 0 ? ((funnelStats.ai_verifying || 0) / funnelStats.uploaded * 100) : 0}%` }}>
                <div className="bar-fill" style={{ width: '100%', backgroundColor: '#f59e0b' }}></div>
              </div>
              <div className="stage-footer">
                {calculateDropoutRate('credit_checked', 'ai_verifying')}% dropout
              </div>
            </div>

            <div className="funnel-arrow">↓</div>

            <div className="funnel-stage">
              <div className="stage-header">
                <span className="stage-name">Verified</span>
                <span className="stage-count">{funnelStats.verified || 0}</span>
              </div>
              <div className="funnel-bar" style={{ width: `${funnelStats.uploaded > 0 ? ((funnelStats.verified || 0) / funnelStats.uploaded * 100) : 0}%` }}>
                <div className="bar-fill" style={{ width: '100%', backgroundColor: '#10b981' }}></div>
              </div>
              <div className="stage-footer">
                {calculateDropoutRate('ai_verifying', 'verified')}% dropout
              </div>
            </div>

            <div className="funnel-arrow">↓</div>

            <div className="funnel-stage">
              <div className="stage-header">
                <span className="stage-name">Ready to Route</span>
                <span className="stage-count">{funnelStats.ready_to_route || 0}</span>
              </div>
              <div className="funnel-bar" style={{ width: `${funnelStats.uploaded > 0 ? ((funnelStats.ready_to_route || 0) / funnelStats.uploaded * 100) : 0}%` }}>
                <div className="bar-fill" style={{ width: '100%', backgroundColor: '#8b5cf6' }}></div>
              </div>
              <div className="stage-footer">
                {((funnelStats.ready_to_route || 0) / (funnelStats.uploaded || 1) * 100).toFixed(1)}% conversion
              </div>
            </div>
          </div>
        </section>

        {/* Two-column layout for metrics and conversations */}
        <div className="dashboard-grid">
          {/* Metrics Panel */}
          <section className="metrics-section">
            <h2>Key Metrics</h2>
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-label">Total Orders</div>
                <div className="metric-value">{Object.values(funnelStats).reduce((a, b) => a + b, 0)}</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Conversion Rate</div>
                <div className="metric-value">
                  {funnelStats.uploaded > 0
                    ? ((funnelStats.ready_to_route || 0) / funnelStats.uploaded * 100).toFixed(1)
                    : 0}%
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-label">In Progress</div>
                <div className="metric-value">{funnelStats.ai_verifying || 0}</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Rejected</div>
                <div className="metric-value">{funnelStats.rejected || 0}</div>
              </div>
            </div>
          </section>

          {/* AI Conversations Feed */}
          <section className="conversations-section">
            <h2>Active Conversations</h2>
            <div className="conversations-feed">
              {conversations.length > 0 ? (
                conversations.map((conv, idx) => (
                  <div key={idx} className="conversation-item">
                    <div className="conversation-header">
                      <span className="order-id">{conv.order_id}</span>
                      <span className="timestamp">{new Date(conv.timestamp).toLocaleTimeString()}</span>
                    </div>
                    <div className="conversation-body">
                      <p className="customer">Customer: {conv.customer_name}</p>
                      <p className="message">{conv.last_message}</p>
                    </div>
                    <div className="conversation-footer">
                      <span className={`status ${conv.status}`}>{conv.status}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <p>No active conversations</p>
                </div>
              )}
            </div>
          </section>
        </div>

        {/* Orders Table */}
        <section className="orders-section">
          <h2>Recent Orders</h2>
          <div className="orders-table-container">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Amount</th>
                  <th>Risk Score</th>
                  <th>Status</th>
                  <th>Payment</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.slice(0, 10).map(order => (
                  <tr key={order.id} onClick={() => setSelectedOrder(order)}>
                    <td className="order-id-cell">{order.order_id}</td>
                    <td>{order.customer_id}</td>
                    <td>{order.customer_phone || '-'}</td>
                    <td className="amount">{formatCurrency(order.amount)}</td>
                    <td>
                      <div className="risk-indicator">
                        <div
                          className="risk-bar"
                          style={{
                            width: '100%',
                            backgroundColor: getRiskColor(order.risk_score || 0)
                          }}
                        >
                          {((order.risk_score || 0) * 100).toFixed(0)}%
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge status-${order.status}`}>
                        {order.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td>{order.payment_method || 'pending'}</td>
                    <td>
                      <button className="action-button">View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="close-button" onClick={() => setSelectedOrder(null)}>×</button>
            <h2>Order Details</h2>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Order ID</label>
                <div className="detail-value">{selectedOrder.order_id}</div>
              </div>
              <div className="detail-item">
                <label>Customer ID</label>
                <div className="detail-value">{selectedOrder.customer_id}</div>
              </div>
              <div className="detail-item">
                <label>Phone</label>
                <div className="detail-value">{selectedOrder.customer_phone || '-'}</div>
              </div>
              <div className="detail-item">
                <label>Amount</label>
                <div className="detail-value">{formatCurrency(selectedOrder.amount)}</div>
              </div>
              <div className="detail-item">
                <label>Status</label>
                <div className="detail-value">
                  <span className={`status-badge status-${selectedOrder.status}`}>
                    {selectedOrder.status.replace('_', ' ')}
                  </span>
                </div>
              </div>
              <div className="detail-item">
                <label>Risk Score</label>
                <div className="detail-value">
                  {((selectedOrder.risk_score || 0) * 100).toFixed(1)}%
                </div>
              </div>
              <div className="detail-item full-width">
                <label>AI Conversation</label>
                <div className="conversation-history">
                  {selectedOrder.ai_conversation && selectedOrder.ai_conversation.length > 0 ? (
                    <ul>
                      {selectedOrder.ai_conversation.map((msg, idx) => (
                        <li key={idx}>
                          <strong>{msg.role}:</strong> {msg.content}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>No conversation history</p>
                  )}
                </div>
              </div>
            </div>
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setSelectedOrder(null)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#e5e7eb',
                  color: '#333',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Close
              </button>
              {selectedOrder.status === 'uploaded' || selectedOrder.status === 'credit_checked' ? (
                <button
                  onClick={handleVerifyOrder}
                  disabled={verifyingOrder === selectedOrder.order_id}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: verifyingOrder === selectedOrder.order_id ? '#9ca3af' : '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: verifyingOrder === selectedOrder.order_id ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  {verifyingOrder === selectedOrder.order_id ? 'Sending...' : '📱 Verify & Send WhatsApp'}
                </button>
              ) : (
                <button
                  disabled
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#d1d5db',
                    color: '#6b7280',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'not-allowed',
                    fontSize: '14px'
                  }}
                >
                  Already Verified
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDashboard;
